import React from "react"
import { Link } from "gatsby"
import Layout from "../components/Layout"
import Pagetitle from "../components/Pagetitle"
import FaqQuestion from "../components/FaqQuestion"
import Seo from "../components/Seo"

function FAQPage() {
  const faq = {
    General: {
      1: [
        "What file format is my product feed?",
        "Your feed is generated as a CSV file. To inspect it, paste your feed URL into a new browser tab — it will download automatically. You can then open it in Excel or Google Sheets to review the contents and check for any issues.",
      ],
      2: [
        "Which platforms does UpFeed support?",
        <>UpFeed supports Google Merchant Center, Meta (Facebook &amp; Instagram), Pinterest, TikTok Shopping Ads, Microsoft Ads (Bing Shopping), and Snapchat Dynamic Ads. The same feed URL works across all platforms — see our <Link to="/getting-started/#platforms">platform guides</Link> for setup instructions for each one.</>,
      ],
      3: [
        "Can I use the same feed URL for multiple platforms?",
        "Yes. Your UpFeed URL is a single feed that all supported platforms can read. You only need to paste it once into each platform's catalog or merchant center setup. There's no need to create separate feeds per platform.",
      ],
      4: [
        "How frequently does my feed update?",
        "Your feed updates automatically every night between 1–3 AM EST. When setting the daily fetch time in each platform, choose a time at least a couple of hours later — we recommend 5 AM EST — so platforms always pull a fully refreshed feed. You can also trigger a manual update at any time using the Generate Feed button on the app homepage.",
      ],
      5: [
        "Does the feed include sale pricing?",
        "Yes. Sale prices are included in the sale_price field by default. When a product is on sale in Shopify, platforms like Google and Meta will automatically show the original price crossed out alongside the sale price in your ads.",
      ],
    },
    "Feed settings": {
      1: [
        "How do I include product variants in my feed?",
        "Enable the Include variants toggle under the Settings tab in your feed. When enabled, each product variant (e.g. size or colour) is listed as a separate item. This is recommended for apparel and footwear as it allows platforms to match ads more precisely to what a shopper is searching for. After changing this setting, click Generate Feed to apply it immediately.",
      ],
      2: [
        "How do I add UTM tracking parameters to my feed links?",
        "You can append UTM parameters to all product links in your feed via the Settings tab. Add your UTM string (e.g. utm_source=google&utm_medium=cpc) and it will be appended to every product URL in the feed. This lets you track shopping ad traffic accurately in Google Analytics or any other analytics tool.",
      ],
      3: [
        "How do I exclude certain products from my feed?",
        "There are three ways to exclude products in the Settings tab. You can exclude by Shopify tag (add a tag like 'no-ads' to any product and enter it in the exclusion field), by product ID, or by variant ID. Alternatively, you can exclude products at the platform level — Google Merchant Center has Feed Rules, and Meta and Pinterest let you create product sets that only include specific items.",
      ],
      4: [
        "Is there a field for additional product information?",
        "Yes. Any tags you add to a product in Shopify appear in the custom_label_0 field of your feed. You can use this to create product sets or groups across multiple brands or categories — for example, tagging products as 'sale' or 'bestseller' and then targeting those groups specifically in your ad campaigns.",
      ],
      5: [
        "Can I customise which fields appear in my feed?",
        "Yes. Under the Mapping tab in your feed you can control which Shopify fields map to each feed attribute. Required fields like id, title, description, price, availability, brand, image_link, and link are pre-mapped automatically. You can also add custom attributes, reorder columns by dragging rows, and remove optional columns you don't need.",
      ],
    },
    Troubleshooting: {
      1: [
        "My feed doesn't contain all my products",
        "By default the feed only includes products marked as available in your Shopify online store channel. Check that missing products are set to Active and available in the Online Store sales channel in Shopify. If products are still missing after confirming availability, please contact us.",
      ],
      2: [
        "My feed doesn't contain product variants",
        "If you want all product variants included in your feed, make sure the Include variants toggle under the Settings tab is enabled. After enabling it, click the Generate Feed button to regenerate your feed. If variants are still missing after doing this, please contact us.",
      ],
      3: [
        "Why are my products being rejected by Google or Meta?",
        <>The most common causes are: missing or incorrect GTINs (barcodes), product titles that don't match the landing page, low-resolution images, or missing <Link to="/getting-started/#googlecats">Google Product Categories</Link>. Check the diagnostics or processing tab in the platform's merchant center for the specific reason. For Google, also make sure your domain is verified and claimed in Merchant Center. If you're unsure, contact us with the error message and we can help.</>,
      ],
      4: [
        "Why aren't my ads showing after connecting my feed?",
        "Platforms review and approve products before serving ads. This typically takes 24–48 hours after your feed is first connected. Microsoft Ads can take up to 3 weeks for an initial review. Ads won't serve until products are approved — check the status in the platform's merchant center or catalog manager. If products are approved but ads still aren't running, check that your campaign is active and has sufficient budget.",
      ],
    },
    Platforms: {
      1: [
        "How do I add my feed to Google Merchant Center?",
        <>See our <Link to="/add-product-feed/google/">Google Merchant Center setup guide</Link> for step-by-step instructions on connecting your feed and running Shopping and Performance Max campaigns.</>,
      ],
      2: [
        "How do I add my feed to Meta (Facebook & Instagram)?",
        <>See our <Link to="/add-product-feed/meta/">Meta &amp; Instagram setup guide</Link> for step-by-step instructions on connecting your feed to Commerce Manager and running Dynamic Ads.</>,
      ],
      3: [
        "How do I add my feed to Pinterest?",
        <>See our <Link to="/add-product-feed/pinterest/">Pinterest setup guide</Link> for step-by-step instructions on connecting your feed to Pinterest Catalogs and running Shopping Ads.</>,
      ],
      4: [
        "How do I add my feed to TikTok?",
        <>See our <Link to="/add-product-feed/tiktok/">TikTok Shopping Ads setup guide</Link> for step-by-step instructions on creating a TikTok product catalog and running Shopping and Dynamic Product Ads.</>,
      ],
      5: [
        "How do I add my feed to Microsoft Ads?",
        <>See our <Link to="/add-product-feed/microsoft-ads/">Microsoft Ads setup guide</Link> for step-by-step instructions on connecting your feed to Microsoft Merchant Center and running Bing Shopping campaigns.</>,
      ],
      6: [
        "How do I add my feed to Snapchat?",
        <>See our <Link to="/add-product-feed/snapchat/">Snapchat Dynamic Ads setup guide</Link> for step-by-step instructions on creating a Snapchat product catalog and running Dynamic Product Ads.</>,
      ],
    },
  }

  return (
    <Layout>
      <Seo
        title="FAQ — UpFeed Product Feed App for Shopify"
        description="Answers to the most common questions about setting up and managing your UpFeed Shopify product feed for Google, Meta, Pinterest, TikTok, Microsoft Ads, and Snapchat."
      />
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <Pagetitle>FAQ</Pagetitle>
            <p className="faq-intro">
              Answers to the most common questions about setting up and managing
              your UpFeed product feed.
            </p>

            <h2 className="faq-category">General</h2>
            {Object.keys(faq.General).map((key) => (
              <FaqQuestion
                key={`general-${key}`}
                question={faq["General"][key][0]}
                answer={faq["General"][key][1]}
              />
            ))}

            <h2 className="faq-category">Feed settings</h2>
            {Object.keys(faq["Feed settings"]).map((key) => (
              <FaqQuestion
                key={`settings-${key}`}
                question={faq["Feed settings"][key][0]}
                answer={faq["Feed settings"][key][1]}
              />
            ))}

            <h2 className="faq-category">Troubleshooting</h2>
            {Object.keys(faq.Troubleshooting).map((key) => (
              <FaqQuestion
                key={`troubleshooting-${key}`}
                question={faq["Troubleshooting"][key][0]}
                answer={faq["Troubleshooting"][key][1]}
              />
            ))}

            <h2 className="faq-category">Platforms</h2>
            {Object.keys(faq.Platforms).map((key) => (
              <FaqQuestion
                key={`platforms-${key}`}
                question={faq["Platforms"][key][0]}
                answer={faq["Platforms"][key][1]}
              />
            ))}

            <div className="faq-contact">
              <h2>Still have a question?</h2>
              <p>
                If you can't find what you're looking for, our support team is
                happy to help. <Link to="/contact/">Get in touch</Link>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default FAQPage